const THEMES = {
  classic: { primary: '#173b67', secondary: '#2a66a5', accent: '#f5c443', background: '#f4f7fb', text: '#10233d', muted: '#58708d' },
  energy: { primary: '#064e3b', secondary: '#0f766e', accent: '#fbbf24', background: '#ecfdf5', text: '#083a2e', muted: '#47756a' },
  clean: { primary: '#292524', secondary: '#57534e', accent: '#ef4444', background: '#fafaf9', text: '#1c1917', muted: '#78716c' },
};

const FONTS = {
  pretendard: { label: 'Pretendard', family: 'Pretendard Variable' },
  suit: { label: 'SUIT', family: 'SUIT Variable' },
  noto: { label: 'Noto Sans KR', family: 'Noto Sans KR Variable' },
};

function roundedRect(context, x, y, width, height, radius) {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
}

function wrapLines(context, text, maxWidth, maxLines) {
  const words = String(text || '').replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  const lines = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (context.measureText(candidate).width <= maxWidth) {
      line = candidate;
      continue;
    }
    if (line) lines.push(line);
    line = word;
    if (lines.length === maxLines - 1) break;
  }
  if (line && lines.length < maxLines) lines.push(line);
  const original = words.join(' ');
  if (lines.join(' ').length < original.length && lines.length) {
    let last = lines[lines.length - 1];
    while (last && context.measureText(`${last}…`).width > maxWidth) last = last.slice(0, -1);
    lines[lines.length - 1] = `${last}…`;
  }
  return lines;
}

function drawLines(context, lines, x, y, lineHeight, width, align = 'left') {
  const anchorX = align === 'center' ? x + width / 2 : align === 'right' ? x + width : x;
  context.textAlign = align;
  lines.forEach((line, index) => context.fillText(line, anchorX, y + index * lineHeight));
  context.textAlign = 'left';
}

function loadImage(source) {
  return new Promise((resolve, reject) => {
    if (!source) return resolve(null);
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function drawCoverImage(context, image, x, y, width, height, crop = {}) {
  const zoom = clamp(Number(crop.imageZoom) || 1, 1, 3);
  const positionX = clamp(Number(crop.imagePositionX) || 50, 0, 100) / 100;
  const positionY = clamp(Number(crop.imagePositionY) || 50, 0, 100) / 100;
  const scale = Math.max(width / image.width, height / image.height) * zoom;
  const renderedWidth = image.width * scale;
  const renderedHeight = image.height * scale;
  const left = x - (renderedWidth - width) * positionX;
  const top = y - (renderedHeight - height) * positionY;
  context.drawImage(image, left, top, renderedWidth, renderedHeight);
}

function resolveTextStyle(card, prefix, defaults, fallbackFontName) {
  const fontName = FONTS[card[`${prefix}Font`]] ? card[`${prefix}Font`] : fallbackFontName;
  const font = FONTS[fontName] || FONTS.pretendard;
  return {
    font: fontName,
    family: font.family,
    size: clamp(Number(card[`${prefix}Size`]) || defaults.size, defaults.min, defaults.max),
    color: card[`${prefix}Color`] || defaults.color,
    align: ['left', 'center', 'right'].includes(card[`${prefix}Align`]) ? card[`${prefix}Align`] : 'left',
    lineHeight: clamp(Number(card[`${prefix}LineHeight`]) || defaults.lineHeight, 1, 2.2),
  };
}

function getCardTextStyles(card, themeName = 'classic', fallbackFontName = 'pretendard') {
  const theme = THEMES[themeName] || THEMES.classic;
  const isCover = card.type === 'cover';
  return {
    title: resolveTextStyle(card, 'title', { size: isCover ? 70 : 62, min: 30, max: 110, color: isCover ? '#ffffff' : theme.text, lineHeight: isCover ? 96 / 70 : 86 / 62 }, fallbackFontName),
    body: resolveTextStyle(card, 'body', { size: 37, min: 18, max: 64, color: isCover ? 'rgba(255,255,255,0.88)' : theme.muted, lineHeight: 59 / 37 }, fallbackFontName),
    highlight: resolveTextStyle(card, 'highlight', { size: 142, min: 64, max: 210, color: theme.primary, lineHeight: 1.1 }, fallbackFontName),
  };
}

function resolveLayoutRegion(region, fallback, minWidth, minHeight = 0) {
  const value = (input, fallbackValue, min, max) => {
    const numeric = Number(input);
    return clamp(Number.isFinite(numeric) ? numeric : fallbackValue, min, max);
  };
  const width = value(region?.width, fallback.width, minWidth, 1080);
  const height = value(region?.height, fallback.height || minHeight, minHeight, 1350);
  return {
    x: value(region?.x, fallback.x, 0, 1080 - width),
    y: value(region?.y, fallback.y, 0, 1350 - height),
    width,
    height,
  };
}

function fillGradient(context, width, height, first, second) {
  const gradient = context.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, first);
  gradient.addColorStop(1, second);
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);
}

export async function renderCardToCanvas(card, index, total, imageSource, themeName = 'classic', fontName = 'pretendard') {
  const font = FONTS[fontName] || FONTS.pretendard;
  const textStyles = getCardTextStyles(card, themeName, fontName);
  const fontText = [card.eyebrow, card.title, card.body, card.highlight, '한전원자력연료'].filter(Boolean).join(' ');
  await document.fonts.ready;
  await Promise.all([...new Set([font.family, textStyles.title.family, textStyles.body.family, textStyles.highlight.family])].map((family) => document.fonts.load(`400 37px "${family}"`, fontText)));
  const fontStack = `"${font.family}", "Malgun Gothic", sans-serif`;
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1350;
  const context = canvas.getContext('2d');
  const theme = THEMES[themeName] || THEMES.classic;
  const image = await loadImage(imageSource).catch(() => null);
  const isCover = card.type === 'cover';

  const photoRegion = resolveLayoutRegion(card.layout?.photo, { x: 82, y: 348, width: 916, height: 390 }, 240, 140);

  if (isCover && image) {
    drawCoverImage(context, image, 0, 0, canvas.width, canvas.height, card);
    const overlay = context.createLinearGradient(0, 250, 0, 1350);
    overlay.addColorStop(0, 'rgba(3, 20, 38, 0.12)');
    overlay.addColorStop(0.58, 'rgba(3, 20, 38, 0.58)');
    overlay.addColorStop(1, 'rgba(3, 20, 38, 0.96)');
    context.fillStyle = overlay;
    context.fillRect(0, 0, 1080, 1350);
  } else {
    context.fillStyle = theme.background;
    context.fillRect(0, 0, 1080, 1350);
    fillGradient(context, 1080, 300, theme.primary, theme.secondary);
    context.globalAlpha = 0.08;
    context.fillStyle = theme.secondary;
    context.beginPath();
    context.arc(980, 1240, 390, 0, Math.PI * 2);
    context.fill();
    context.globalAlpha = 1;
  }

  context.textBaseline = 'top';
  context.fillStyle = isCover ? '#ffffff' : index === 0 ? '#ffffff' : theme.accent;
  context.font = `700 30px ${fontStack}`;
  context.fillText(card.eyebrow || 'KNFC NEWS', 82, 76);

  if (!isCover && image && card.type !== 'number') {
    roundedRect(context, photoRegion.x, photoRegion.y, photoRegion.width, photoRegion.height, 32);
    context.save();
    context.clip();
    drawCoverImage(context, image, photoRegion.x, photoRegion.y, photoRegion.width, photoRegion.height, card);
    context.restore();
  }

  let titleY = isCover ? 760 : image && card.type !== 'number' ? 800 : 390;
  if (card.type === 'number' && card.highlight) {
    context.fillStyle = textStyles.highlight.color;
    context.font = `900 ${textStyles.highlight.size}px "${textStyles.highlight.family}", "Malgun Gothic", sans-serif`;
    context.textAlign = textStyles.highlight.align;
    const highlightX = textStyles.highlight.align === 'center' ? 540 : textStyles.highlight.align === 'right' ? 998 : 82;
    context.fillText(card.highlight, highlightX, 390);
    context.textAlign = 'left';
    titleY = 390 + textStyles.highlight.size * textStyles.highlight.lineHeight + 44;
  }
  if (card.type === 'quote') {
    context.fillStyle = theme.accent;
    context.font = '900 160px Georgia, serif';
    context.fillText('“', 70, titleY - 95);
    titleY += 80;
  }

  const textRegion = resolveLayoutRegion(card.layout?.text, { x: 82, y: titleY, width: 916 }, 240);
  context.fillStyle = textStyles.title.color;
  context.font = `800 ${textStyles.title.size}px "${textStyles.title.family}", "Malgun Gothic", sans-serif`;
  const titleLines = wrapLines(context, card.title, textRegion.width, isCover ? 3 : 2);
  const titleLineHeight = textStyles.title.size * textStyles.title.lineHeight;
  drawLines(context, titleLines, textRegion.x, textRegion.y, titleLineHeight, textRegion.width, textStyles.title.align);

  const bodyY = textRegion.y + titleLines.length * titleLineHeight + 42;
  context.fillStyle = textStyles.body.color;
  context.font = `400 ${textStyles.body.size}px "${textStyles.body.family}", "Malgun Gothic", sans-serif`;
  const bodyLines = wrapLines(context, card.body, textRegion.width, isCover ? 4 : 6);
  drawLines(context, bodyLines, textRegion.x, bodyY, textStyles.body.size * textStyles.body.lineHeight, textRegion.width, textStyles.body.align);

  context.fillStyle = isCover ? 'rgba(255,255,255,0.72)' : theme.muted;
  context.font = `600 25px ${fontStack}`;
  context.fillText('한전원자력연료  |  www.knfc.co.kr', 82, 1260);
  context.textAlign = 'right';
  context.fillText(`${String(index + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`, 998, 1260);
  context.textAlign = 'left';
  return canvas;
}

export function canvasToBlob(canvas, type = 'image/jpeg', quality = 0.94) {
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('이미지 생성 실패')), type, quality));
}

export { FONTS, THEMES, getCardTextStyles, resolveLayoutRegion };
